/* temi-carousel.js — the two buttons and the count for `temi-product-story`'s carousel.
   The track is a native scroll-snap scroller; this file never moves anything the browser can't
   already move by trackpad, touch or keyboard. It only:
     · steps one photograph per button press, wrapping at either end (so no disabled state —
       §6 has no ink for one);
     · keeps the count on the first photograph whose left edge is in view;
     · hides the control row when every photograph already fits, and marks the element
       `data-fits` so the CSS can close the row's ragged end;
     · opens and steps the lightbox (below). */
class TemiCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.controls = this.querySelector('[data-controls]');
    this.current = this.querySelector('[data-current]');
    if (!this.track || this.slides.length === 0) return;

    this.querySelector('[data-prev]').addEventListener('click', () => this.step(-1));
    this.querySelector('[data-next]').addEventListener('click', () => this.step(1));

    let frame = null;
    this.track.addEventListener('scroll', () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        this.update();
      });
    }, { passive: true });

    // Images are lazy and set their own widths, so overflow is only known once they load.
    this.setUpLightbox();

    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.track);
    this.slides.forEach((slide) => this.resizeObserver.observe(slide));
    this.update();
  }

  /* ── The lightbox (2026-09-30) ──────────────────────────────────────────────────────────
     A native <dialog>: showModal() puts it in the top layer, traps focus, closes on Esc and
     hands focus back to the photograph that opened it. This file only fills it and steps it. */
  setUpLightbox() {
    this.dialog = this.querySelector('[data-lightbox]');
    if (!this.dialog) return;
    this.openers = [...this.querySelectorAll('[data-open]')];
    this.lightboxImage = this.dialog.querySelector('[data-lightbox-image]');
    this.lightboxCurrent = this.dialog.querySelector('[data-lightbox-current]');
    this.lightboxIndex = 0;

    this.openers.forEach((button) =>
      button.addEventListener('click', () => this.openLightbox(Number(button.dataset.open)))
    );
    this.dialog.querySelector('[data-lightbox-prev]').addEventListener('click', () => this.showInLightbox(this.lightboxIndex - 1));
    this.dialog.querySelector('[data-lightbox-next]').addEventListener('click', () => this.showInLightbox(this.lightboxIndex + 1));
    this.dialog.querySelector('[data-lightbox-close]').addEventListener('click', () => this.dialog.close());

    // Clicking the stage outside the photograph closes; the photograph itself does not.
    this.dialog.querySelector('[data-stage]').addEventListener('click', (event) => {
      if (event.target !== this.lightboxImage) this.dialog.close();
    });

    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') this.showInLightbox(this.lightboxIndex - 1);
      if (event.key === 'ArrowRight') this.showInLightbox(this.lightboxIndex + 1);
    });

    // Leave the carousel where the viewer left the lightbox.
    this.dialog.addEventListener('close', () => {
      this.track.scrollTo({ left: this.slides[this.lightboxIndex].offsetLeft });
    });
  }

  openLightbox(index) {
    this.showInLightbox(index);
    this.dialog.showModal();
  }

  showInLightbox(index) {
    const count = this.openers.length;
    this.lightboxIndex = (index + count) % count; // wraps, like the carousel
    const opener = this.openers[this.lightboxIndex];
    this.lightboxImage.src = opener.dataset.full;
    this.lightboxImage.alt = opener.dataset.alt || '';
    this.lightboxCurrent.textContent = String(this.lightboxIndex + 1);
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  }

  index() {
    const left = this.track.scrollLeft;
    // The slide whose start is closest to the track's scroll position.
    let best = 0;
    let bestDistance = Infinity;
    this.slides.forEach((slide, i) => {
      const distance = Math.abs(slide.offsetLeft - left);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    return best;
  }

  atEnd() {
    return this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;
  }

  step(direction) {
    let target = this.index() + direction;
    if (direction > 0 && this.atEnd()) target = 0;
    if (target < 0) target = this.slides.length - 1;
    if (target >= this.slides.length) target = 0;
    this.track.scrollTo({ left: this.slides[target].offsetLeft });
  }

  update() {
    const overflows = this.track.scrollWidth > this.track.clientWidth + 1;
    this.controls.hidden = !overflows;
    this.toggleAttribute('data-fits', !overflows);
    if (this.current) this.current.textContent = String(this.index() + 1);
  }
}

if (!customElements.get('temi-carousel')) {
  customElements.define('temi-carousel', TemiCarousel);
}
